import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Globe, Landmark, CreditCard, ShieldCheck, RefreshCw, Plus, Trash2, Save, Building2, Power,
} from "lucide-react";

/**
 * THOM Verify™ Africa · Country Configuration Console
 *
 * Config-driven, MongoDB-backed. Every country carries its own registrar,
 * police-database, currency, payment gateways, VIN format, escrow flag,
 * verification services, and cities — no application-code change required
 * to launch a new market.
 *
 * RBAC:
 *  - super_admin: create / edit / delete / toggle any country + gateways
 *  - country_admin: edit branding / services / cities / gateways for OWN country only
 *  - executive / others: read-only view
 */

const ROLE_CAN_WRITE = new Set(["super_admin", "admin", "country_admin"]);
const ROLE_IS_HQ = new Set(["super_admin", "admin"]);

export default function CountryConfig() {
  const { user } = useAuth();
  const canWrite = user && ROLE_CAN_WRITE.has(user.role);
  const isHq = user && ROLE_IS_HQ.has(user.role);

  const [matrix, setMatrix] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedIso, setSelectedIso] = useState("ZW");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [editDraft, setEditDraft] = useState(null);          // partial edit for selected country
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(defaultDraft());

  const load = async () => {
    setBusy(true);
    try {
      const [m, b] = await Promise.all([
        api.get("/gateway-admin/matrix"),
        api.get("/gateway-admin/banks"),
      ]);
      setMatrix(m.data);
      setBanks(b.data.items || []);
      // if country_admin, force selection to their own country
      if (user?.role === "country_admin" && user.country) setSelectedIso(user.country.toUpperCase());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load configuration");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.role]);

  const visibleCountries = useMemo(() => {
    const rows = matrix?.countries || [];
    if (user?.role === "country_admin" && user.country) {
      return rows.filter(c => c.iso === user.country.toUpperCase());
    }
    return rows;
  }, [matrix, user?.role, user?.country]);

  const country = useMemo(
    () => visibleCountries.find(c => c.iso === selectedIso) || visibleCountries[0],
    [visibleCountries, selectedIso]
  );

  useEffect(() => { setEditDraft(country ? {
    name: country.name,
    currency: country.currency,
    registration_authority: country.registration_authority || "",
    police_database: country.police_database || "",
    vin_hint: country.vin_hint || "",
    tax_rule: country.tax_rule || "",
    cities: (country.cities || []).join(", "),
    languages: (country.languages || []).join(", "),
    verification_services: (country.verification_services || []).join(", "),
    escrow_enabled: !!country.escrow_enabled,
    brand_accent: country.brand?.accent || "",
    brand_tagline: country.brand?.tagline || "",
  } : null); }, [country?.iso]);

  const filteredCountries = useMemo(
    () => visibleCountries.filter(c => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.iso.toLowerCase() === q.toLowerCase()),
    [visibleCountries, q]
  );
  const countryBanks = useMemo(
    () => banks.filter(b => !country || b.country === country.iso),
    [banks, country]
  );

  const toggleGateway = async (key, enabled) => {
    if (!canWrite) return toast.error("Read-only");
    try {
      await api.post(`/gateway-admin/gateway/${key}/toggle`, { enabled });
      toast.success(`${key} · ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const savePatch = async () => {
    if (!country || !editDraft) return;
    try {
      const patch = {
        name: editDraft.name,
        currency: editDraft.currency,
        registration_authority: editDraft.registration_authority,
        police_database: editDraft.police_database,
        vin_hint: editDraft.vin_hint,
        tax_rule: editDraft.tax_rule,
        cities: splitList(editDraft.cities),
        languages: splitList(editDraft.languages),
        verification_services: splitList(editDraft.verification_services),
        escrow_enabled: !!editDraft.escrow_enabled,
        brand: { accent: editDraft.brand_accent || "", tagline: editDraft.brand_tagline || "" },
      };
      await api.patch(`/countries/${country.iso}`, patch);
      toast.success(`${country.name} · saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (nextActive) => {
    if (!isHq) return toast.error("Only Super Admin (Africa HQ) can enable/disable a country");
    try {
      await api.post(`/countries/${country.iso}/toggle`, { is_active: nextActive });
      toast.success(`${country.name} · ${nextActive ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteCountry = async () => {
    if (!isHq) return;
    if (!confirm(`Delete country ${country.name}? This is irreversible.`)) return;
    try {
      await api.delete(`/countries/${country.iso}`);
      toast.success(`${country.name} · deleted`);
      setSelectedIso("ZW");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const createCountry = async () => {
    try {
      await api.post("/countries", {
        iso: (createDraft.iso || "").toUpperCase(),
        name: createDraft.name,
        currency: createDraft.currency,
        registration_authority: createDraft.registration_authority,
        police_database: createDraft.police_database,
        vin_hint: createDraft.vin_hint,
        tax_rule: createDraft.tax_rule,
        cities: splitList(createDraft.cities),
        languages: splitList(createDraft.languages),
        gateways: splitList(createDraft.gateways),
        verification_services: splitList(createDraft.verification_services),
        escrow_enabled: !!createDraft.escrow_enabled,
        is_active: true,
        brand: { accent: createDraft.brand_accent, tagline: createDraft.brand_tagline },
      });
      toast.success(`${createDraft.name} · created`);
      setCreateOpen(false);
      setCreateDraft(defaultDraft());
      setSelectedIso((createDraft.iso || "").toUpperCase());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="kicker">Executive · Country Configuration</div>
            <h1 className="display text-4xl">Africa-wide Trust Infrastructure Console</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
              Every African market plugs into THOM Verify™ through this console. Each country carries
              its own registrar, police database, currency, payment gateways, VIN format, escrow
              module, verification services and inspector network — persisted to MongoDB so no code
              change is required to launch or update a market.
            </p>
            {user && (
              <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground mt-2">
                Signed in as <span className="text-primary">{user.role}</span>{user.country ? ` · country ${user.country}` : " · Africa HQ (all countries)"}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="config-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
            </Button>
            <Link to="/ops"><Button variant="outline" size="sm">Ops Centre</Button></Link>
            {isHq && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="config-new-country">
                    <Plus className="h-3.5 w-3.5 mr-1" /> New country
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" data-testid="config-new-dialog">
                  <DialogHeader><DialogTitle>Register a new African market</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <Field label="ISO alpha-2" value={createDraft.iso} onChange={v => setCreateDraft({ ...createDraft, iso: v.toUpperCase().slice(0,2) })} data-testid="config-new-iso" />
                    <Field label="Country name" value={createDraft.name} onChange={v => setCreateDraft({ ...createDraft, name: v })} data-testid="config-new-name" />
                    <Field label="Currency" value={createDraft.currency} onChange={v => setCreateDraft({ ...createDraft, currency: v.toUpperCase() })} />
                    <Field label="Tax rule" value={createDraft.tax_rule} onChange={v => setCreateDraft({ ...createDraft, tax_rule: v })} />
                    <Field label="Registration authority" value={createDraft.registration_authority} onChange={v => setCreateDraft({ ...createDraft, registration_authority: v })} span={2} />
                    <Field label="Police database" value={createDraft.police_database} onChange={v => setCreateDraft({ ...createDraft, police_database: v })} span={2} />
                    <Field label="VIN hint" value={createDraft.vin_hint} onChange={v => setCreateDraft({ ...createDraft, vin_hint: v })} span={2} />
                    <Field label="Cities (comma-separated)" value={createDraft.cities} onChange={v => setCreateDraft({ ...createDraft, cities: v })} span={2} />
                    <Field label="Languages (comma-separated)" value={createDraft.languages} onChange={v => setCreateDraft({ ...createDraft, languages: v })} />
                    <Field label="Gateways (comma-separated keys)" value={createDraft.gateways} onChange={v => setCreateDraft({ ...createDraft, gateways: v })} />
                    <Field label="Verification services" value={createDraft.verification_services} onChange={v => setCreateDraft({ ...createDraft, verification_services: v })} span={2} />
                    <Field label="Brand accent (hex)" value={createDraft.brand_accent} onChange={v => setCreateDraft({ ...createDraft, brand_accent: v })} />
                    <Field label="Brand tagline" value={createDraft.brand_tagline} onChange={v => setCreateDraft({ ...createDraft, brand_tagline: v })} />
                  </div>
                  <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                    <Button onClick={createCountry} data-testid="config-new-save">Create country</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Country list */}
          <aside className="border border-border bg-card">
            <div className="p-3 border-b border-border">
              <Input placeholder="Search country..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="config-country-search" />
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {filteredCountries.map(c => (
                <button
                  key={c.iso}
                  onClick={() => setSelectedIso(c.iso)}
                  data-testid={`config-country-${c.iso}`}
                  className={`w-full text-left px-4 py-3 border-b border-border/60 flex items-center justify-between hover:bg-muted/50 transition-colors ${selectedIso === c.iso ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}
                >
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {c.name}
                      {!c.is_active && <span className="mono text-[9px] uppercase px-1 py-0.5 bg-red-500/10 text-red-700 rounded-sm">Inactive</span>}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.iso} · {c.currency}</div>
                  </div>
                  <span className="mono text-[10px] text-muted-foreground">{(c.gateways || []).filter(g => g.enabled_for_country).length}/{(c.gateways || []).length}</span>
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <div className="text-xs text-muted-foreground p-4">No countries match "{q}".</div>
              )}
            </div>
          </aside>

          {/* Country detail */}
          <section>
            {!country && <div className="mono text-sm text-muted-foreground">Loading matrix…</div>}
            {country && editDraft && (
              <div className="space-y-5">
                {/* Country meta + editable form */}
                <div className="border border-border bg-card p-5" data-testid="config-country-meta">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="kicker flex items-center gap-2"><Globe className="h-3 w-3" />Deployment</div>
                      <h2 className="text-2xl font-semibold mt-1">{country.name}</h2>
                      <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{country.iso} · {country.currency} · Tax {country.tax_rule}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isHq && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => toggleActive(!country.is_active)} data-testid="config-toggle-active">
                            <Power className={`h-3.5 w-3.5 mr-1 ${country.is_active ? "text-emerald-600" : "text-red-600"}`} />
                            {country.is_active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={deleteCountry} data-testid="config-delete">
                            <Trash2 className="h-3.5 w-3.5 mr-1 text-red-600" /> Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 mt-5">
                    <Field readOnly={!canWrite} label="Country name" value={editDraft.name} onChange={v => setEditDraft({ ...editDraft, name: v })} data-testid="config-edit-name" />
                    <Field readOnly={!canWrite} label="Currency" value={editDraft.currency} onChange={v => setEditDraft({ ...editDraft, currency: v.toUpperCase() })} />
                    <Field readOnly={!canWrite} label="Registration authority" value={editDraft.registration_authority} onChange={v => setEditDraft({ ...editDraft, registration_authority: v })} span={2} />
                    <Field readOnly={!canWrite} label="Police database" value={editDraft.police_database} onChange={v => setEditDraft({ ...editDraft, police_database: v })} span={2} />
                    <Field readOnly={!canWrite} label="VIN hint" value={editDraft.vin_hint} onChange={v => setEditDraft({ ...editDraft, vin_hint: v })} span={2} />
                    <Field readOnly={!canWrite} label="Cities (comma-separated)" value={editDraft.cities} onChange={v => setEditDraft({ ...editDraft, cities: v })} span={2} />
                    <Field readOnly={!canWrite} label="Languages (comma-separated)" value={editDraft.languages} onChange={v => setEditDraft({ ...editDraft, languages: v })} />
                    <Field readOnly={!canWrite} label="Tax rule" value={editDraft.tax_rule} onChange={v => setEditDraft({ ...editDraft, tax_rule: v })} />
                    <Field readOnly={!canWrite} label="Verification services" value={editDraft.verification_services} onChange={v => setEditDraft({ ...editDraft, verification_services: v })} span={2} placeholder="autoverify, propertyverify, businessverify" />
                    <Field readOnly={!canWrite} label="Brand accent (hex)" value={editDraft.brand_accent} onChange={v => setEditDraft({ ...editDraft, brand_accent: v })} />
                    <Field readOnly={!canWrite} label="Brand tagline" value={editDraft.brand_tagline} onChange={v => setEditDraft({ ...editDraft, brand_tagline: v })} />
                    <div className="md:col-span-2 flex items-center justify-between border border-dashed border-border rounded-sm px-3 py-2 mt-1">
                      <div>
                        <div className="text-sm font-medium">Escrow module</div>
                        <div className="text-[11px] text-muted-foreground">Enables THOM Escrow™ workflow (deposit → inspection → legal → release) for this market.</div>
                      </div>
                      <Switch checked={!!editDraft.escrow_enabled} onCheckedChange={v => setEditDraft({ ...editDraft, escrow_enabled: v })} disabled={!canWrite} data-testid="config-toggle-escrow" />
                    </div>
                  </div>
                  {canWrite && (
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" onClick={savePatch} data-testid="config-save-country"><Save className="h-3.5 w-3.5 mr-1" /> Save changes</Button>
                      <span className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground self-center">All edits audited · actor · IP · timestamp captured</span>
                    </div>
                  )}
                </div>

                {/* Gateways */}
                <div className="border border-border bg-card">
                  <div className="p-5 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-primary" />
                      <div>
                        <h3 className="font-semibold">Payment gateways</h3>
                        <div className="text-xs text-muted-foreground">Toggle each adapter live. Phase 2 adapters go live automatically the moment their env credentials are set.</div>
                      </div>
                    </div>
                    <div className="mono text-[11px] text-muted-foreground">
                      {(country.gateways || []).filter(g => g.enabled).length} enabled · {(country.gateways || []).length} declared
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {(country.gateways || []).map(g => (
                      <div key={g.key} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`config-gateway-${g.key}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{g.label}</span>
                            <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${g.phase === 1 ? "bg-primary/10 text-primary" : "bg-amber-500/10 text-amber-700"}`}>
                              Phase {g.phase}
                            </span>
                            <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${g.mode === "live" ? "bg-emerald-500/10 text-emerald-700" : "bg-slate-500/10 text-slate-700"}`}>
                              {g.mode}
                            </span>
                          </div>
                          <div className="text-[11.5px] text-muted-foreground mt-1">
                            {g.region} · {(g.methods || []).join(" · ") || "—"}
                          </div>
                          <div className="mono text-[10.5px] text-muted-foreground/80 mt-0.5">Accepts: {(g.currencies || []).slice(0, 6).join(", ")}{(g.currencies || []).length > 6 ? "…" : ""}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] text-muted-foreground">Enabled</span>
                          <Switch
                            checked={!!g.enabled}
                            onCheckedChange={(v) => toggleGateway(g.key, v)}
                            disabled={!isHq}
                            data-testid={`config-toggle-${g.key}`}
                          />
                        </div>
                      </div>
                    ))}
                    {(country.gateways || []).length === 0 && (
                      <div className="p-4 text-sm text-muted-foreground">No gateways declared for {country.name} yet.</div>
                    )}
                  </div>
                </div>

                {/* Banks */}
                <div className="border border-border bg-card">
                  <div className="p-5 border-b border-border flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-primary" />
                    <div>
                      <h3 className="font-semibold">Banking institutions</h3>
                      <div className="text-xs text-muted-foreground">Wire recipients under the bank_transfer adapter's ops-confirms-on-receipt flow until each bank publishes a live API.</div>
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {countryBanks.map(b => (
                      <div key={b.key} className="p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`config-bank-${b.key}`}>
                        <div>
                          <div className="text-sm font-medium">{b.name}</div>
                          <div className="mono text-[10.5px] text-muted-foreground mt-0.5">{b.country}{b.swift ? ` · SWIFT ${b.swift}` : ""}{b.notes ? ` · ${b.notes}` : ""}</div>
                        </div>
                        <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${b.api_ready ? "bg-emerald-500/10 text-emerald-700" : "bg-slate-500/10 text-slate-700"}`}>
                          {b.api_ready ? "API ready" : "Ops confirms"}
                        </span>
                      </div>
                    ))}
                    {countryBanks.length === 0 && (
                      <div className="p-4 text-sm text-muted-foreground">No banks registered for {country.name} yet.</div>
                    )}
                  </div>
                </div>

                {/* Cities */}
                <div className="border border-border bg-card p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <h3 className="font-semibold">Inspection cities</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(country.cities || []).map(c => (
                      <span key={c} className="text-[11.5px] px-2.5 py-1 border border-border rounded-sm mono uppercase tracking-widest">{c}</span>
                    ))}
                    {(country.cities || []).length === 0 && <span className="text-xs text-muted-foreground">No cities configured</span>}
                  </div>
                </div>

                {/* Country-scoped drill-down */}
                <div className="border border-primary/30 bg-primary/[0.03] p-5 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <div>
                      <div className="text-sm font-medium">{country.name} · Operations Workspace</div>
                      <div className="text-xs text-muted-foreground">Open the verification request triage filtered to this country.</div>
                    </div>
                  </div>
                  <Link to={`/admin/verification-requests?country=${encodeURIComponent(country.name)}`}>
                    <Button size="sm" data-testid="config-drilldown">Open triage →</Button>
                  </Link>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function defaultDraft() {
  return {
    iso: "", name: "", currency: "USD",
    registration_authority: "", police_database: "", vin_hint: "",
    tax_rule: "", cities: "", languages: "en",
    gateways: "bank_transfer, cash", verification_services: "autoverify",
    escrow_enabled: true, brand_accent: "#D4AF37", brand_tagline: "",
  };
}

function splitList(s) {
  return (s || "")
    .split(",")
    .map(x => x.trim())
    .filter(Boolean);
}

const Field = ({ label, value, onChange, readOnly, span, placeholder, ...rest }) => (
  <div className={span === 2 ? "md:col-span-2" : ""}>
    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    <Input
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      disabled={readOnly}
      placeholder={placeholder}
      className="h-8 text-[12.5px]"
      {...rest}
    />
  </div>
);
